/**
 * constants/ai.ts
 * AI integration domain constants and display data
 *
 * Provides runtime constants for the AI domain including:
 * - Provider and role arrays with compile-time type safety
 * - Display maps for providers, roles, and validation statuses
 * - Model metadata with context window and output token limits
 * - Per-provider default model identifiers
 *
 * These constants correspond to the pure type definitions in `src/types/shared/AI.ts`.
 *
 * ARCHITECTURAL CONSTRAINTS:
 * - Zero cross-domain imports (only `import type` from `types/`)
 * - No imports from `utils/` or other domain constants
 * - No side effects, pure data only
 * - All exports use `as const satisfies` for compile-time type safety
 *
 * TESTING GUIDELINES:
 * - Do NOT import from this file in tests to assert behavior
 * - Prefer importing parametric utils and passing these maps as inputs
 * - This keeps tests decoupled from constants and focused on logic
 */

// Internal absolute imports (type-only)
import type {
  AIModel,
  AIProviderType,
  AIRole,
  AIValidationStatus
} from '@/types/shared/AI';

// ─── Provider Constants ──────────────────────────────────────────────────────

/**
 * All supported AI providers
 * Ordered alphabetically for stable iteration
 */
export const AI_PROVIDER_TYPES = [
  'anthropic',
  'openai'
] as const satisfies readonly AIProviderType[];

// keep alphabetical by key for drift hygiene
/** Display names for AI providers */
export const AI_PROVIDER_DISPLAY_MAP = {
  anthropic: 'Anthropic',
  openai: 'OpenAI'
} as const satisfies Readonly<Record<AIProviderType, string>>;

// ─── Role Constants ──────────────────────────────────────────────────────────

/**
 * All message roles
 * Ordered alphabetically for stable iteration
 */
export const AI_ROLES = [
  'assistant',
  'system',
  'user'
] as const satisfies readonly AIRole[];

// keep alphabetical by key for drift hygiene
/** Display names for message roles */
export const AI_ROLE_DISPLAY_MAP = {
  assistant: 'Assistant',
  system: 'System',
  user: 'User'
} as const satisfies Readonly<Record<AIRole, string>>;

// ─── Validation Status Constants ─────────────────────────────────────────────

/**
 * All API key validation statuses
 * Ordered alphabetically for stable iteration
 */
export const AI_VALIDATION_STATUSES = [
  'error',
  'invalid',
  'untested',
  'valid'
] as const satisfies readonly AIValidationStatus[];

// keep alphabetical by key for drift hygiene
/** Display names for validation statuses */
export const AI_VALIDATION_STATUS_DISPLAY_MAP = {
  error: 'Error',
  invalid: 'Invalid',
  untested: 'Not Tested',
  valid: 'Valid'
} as const satisfies Readonly<Record<AIValidationStatus, string>>;

// ─── Model Metadata ──────────────────────────────────────────────────────────

/**
 * Available AI models with capabilities metadata
 *
 * Model data is intentionally easy to update as providers release new models.
 * Context windows and output limits are conservative estimates — actual limits
 * may vary with provider API versions.
 *
 * Ordered by provider (alphabetical), then by capability (descending)
 */
export const AI_MODELS: readonly AIModel[] = [
  // Anthropic models
  {
    id: 'claude-sonnet-4-5-20250929',
    name: 'Claude Sonnet 4.5',
    provider: 'anthropic',
    contextWindow: 200_000,
    maxOutputTokens: 8_192
  },
  {
    id: 'claude-haiku-4-5-20251001',
    name: 'Claude Haiku 4.5',
    provider: 'anthropic',
    contextWindow: 200_000,
    maxOutputTokens: 8_192
  },
  // OpenAI models
  {
    id: 'gpt-4o',
    name: 'GPT-4o',
    provider: 'openai',
    contextWindow: 128_000,
    maxOutputTokens: 16_384
  },
  {
    id: 'gpt-4o-mini',
    name: 'GPT-4o Mini',
    provider: 'openai',
    contextWindow: 128_000,
    maxOutputTokens: 16_384
  }
] as const;

// ─── Default Models ──────────────────────────────────────────────────────────

// keep alphabetical by key for drift hygiene
/**
 * Default model ID per provider
 *
 * Used when no model is explicitly selected by the user.
 */
export const AI_DEFAULT_MODEL_MAP = {
  anthropic: 'claude-sonnet-4-5-20250929',
  openai: 'gpt-4o'
} as const satisfies Readonly<Record<AIProviderType, string>>;

// ─── Compile-Time Exhaustiveness Checks ──────────────────────────────────────

type _AllAIProviderTypesCovered =
  Exclude<AIProviderType, (typeof AI_PROVIDER_TYPES)[number]> extends never ? true : never;

type _AllAIRolesCovered =
  Exclude<AIRole, (typeof AI_ROLES)[number]> extends never ? true : never;

type _AllAIValidationStatusesCovered =
  Exclude<AIValidationStatus, (typeof AI_VALIDATION_STATUSES)[number]> extends never ? true : never;

type _AllAIProviderDisplayCovered =
  Exclude<AIProviderType, keyof typeof AI_PROVIDER_DISPLAY_MAP> extends never ? true : never;

type _AllAIRoleDisplayCovered =
  Exclude<AIRole, keyof typeof AI_ROLE_DISPLAY_MAP> extends never ? true : never;

type _AllAIValidationStatusDisplayCovered =
  Exclude<AIValidationStatus, keyof typeof AI_VALIDATION_STATUS_DISPLAY_MAP> extends never ? true : never;

type _AllAIDefaultModelsCovered =
  Exclude<AIProviderType, keyof typeof AI_DEFAULT_MODEL_MAP> extends never ? true : never;

/**
 * @internal
 * Compile-time coverage checks only - bundled export to eliminate unused variable warnings
 * while maintaining drift protection. Do NOT re-export from barrels.
 */
export type _AIConstantChecks = [
  _AllAIProviderTypesCovered,
  _AllAIRolesCovered,
  _AllAIValidationStatusesCovered,
  _AllAIProviderDisplayCovered,
  _AllAIRoleDisplayCovered,
  _AllAIValidationStatusDisplayCovered,
  _AllAIDefaultModelsCovered
];
